<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { ITrackerDealForm, ITrackerDealFormComposableOptions } from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import type { IChangeHistoryEntry } from '@modules/change-history/components/VChangeHistory/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME } from '@modules/affiliates-approved/constants';
import VAffiliateTrackersSettingsTabs from '@modules/affiliates-approved/submodules/trackers-settings/components/VAffiliateTrackersSettingsTabs/VAffiliateTrackersSettingsTabs.vue';
import {

  useTrackerDealForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS } from '@modules/affiliates-approved/submodules/trackers-settings/constants';
import VChangeHistory from '@modules/change-history/components/VChangeHistory/VChangeHistory.vue';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useI18n } from '@modules/i18n/composables';
import { useMerchantIdStore } from '@modules/merchant/store';
import { isString } from '@utils/guards';

import { AFFILIATE_SUBAFFILIATE_SETTINGS, TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_SUBAFFILIATE_CONDITION } from '../../constants';
import { useAffiliateSubaffiliateConditionCreateMutation, useAffiliateSubaffiliateConditionDeleteMutation } from '../../mutations';
import { useAffiliateShowSubaffiliateConditionQuery } from '../../queries';
import { SUBAFFILIATE_HISTORY_CHANGES_EDIT_ROUTE_NAME } from '../../submodules/subaffiliate-history-changes-edit/constants';

const route = useRoute();
const router = useRouter();
const { getTranslation } = useI18n();
const { formatMonthDate } = useFormatMonthDate();
const merchantId = toRef(useMerchantIdStore(), 'merchantId');

const affiliateId = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));

const { data, isLoading, setData } = useAffiliateShowSubaffiliateConditionQuery(affiliateId);

const {
  mutateAsync,
  data: createResponse,
  isLoading: isCreateLoading,
  errors,
  clearError,
} = useAffiliateSubaffiliateConditionCreateMutation();

const { mutateAsync: removeHistoryItem } = useAffiliateSubaffiliateConditionDeleteMutation();

const formOptions = computed<ITrackerDealFormComposableOptions>(() => ({
  conditions: data.value?.subaffiliate_conditions ?? [],
  isLoading: isLoading.value,
  conditionValue: data.value?.subaffiliate_condition.id,
  conditionLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_SUBAFFILIATE_CONDITION, 'FORM_SUBAFFILIATE_CONDITION_ID').value,
  dateLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_SUBAFFILIATE_CONDITION, 'FORM_DATE').value,
}));

const historyOfTrackerChanges = computed(() => data.value?.subaffiliate_condition.history_of_subaffiliates_change ?? []);

const { formSchema } = useTrackerDealForm(formOptions);

async function onSubmit({ condition_id, date }: ITrackerDealForm): Promise<void> {
  try {
    await mutateAsync({
      history_of_subaffiliates_change: {
        affiliate_id: affiliateId.value,
        subaffiliate_condition_id: condition_id,
        date: formatMonthDate(date),
      },
    });

    updateHistoryOfTrackerChanges(createResponse.value?.history_of_subaffiliates_changes);
  } catch {}
}

function onHistoryEdit(entry: IChangeHistoryEntry): void {
  void router.push({ name: SUBAFFILIATE_HISTORY_CHANGES_EDIT_ROUTE_NAME, params: { merchantId: merchantId.value, itemId: entry.id } });
}

function updateHistoryOfTrackerChanges(list?: IChangeHistoryEntry[]): void {
  setData((queryData) => {
    if (!queryData) {
      return queryData;
    }

    return {
      ...queryData,
      subaffiliate_condition: {
        ...queryData.subaffiliate_condition,
        history_of_subaffiliates_change: list ?? queryData.subaffiliate_condition.history_of_subaffiliates_change,
      },
    };
  });
}

async function onHistoryDelete(entry: IChangeHistoryEntry): Promise<void> {
  try {
    await removeHistoryItem(entry.id);

    updateHistoryOfTrackerChanges(historyOfTrackerChanges.value.filter(({ id }) => id !== entry.id));
  } catch {}
}
</script>

<template>
  <VCard v-data-qa-auto-id="'TrackersSettingsSubaffiliateConditionPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_HEADER')"
  >
    <VAffiliateTrackersSettingsTabs />
    <VForm
      :schema="formSchema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_SAVE')"
      :is-loading="isCreateLoading"
      :custom-errors="errors"
      @submit="onSubmit"
      @interacted="clearError"
    >
      <template #secondaryBtn>
        <VBackButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_BACK')"
          skip-history
          :to="{
            name: ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME,
          }"
        />
      </template>
    </VForm>
  </VCard>

  <VLoader
    :loading="isLoading"
    class="mt-2"
  >
    <VChangeHistory
      :list="historyOfTrackerChanges"
      :edit-permission="AFFILIATE_SUBAFFILIATE_SETTINGS.HISTORY_CHANGES_EDIT"
      :delete-permission="AFFILIATE_SUBAFFILIATE_SETTINGS.HISTORY_CHANGES_DELETE"
      @edit="onHistoryEdit"
      @delete="onHistoryDelete"
    />
  </VLoader>
</template>
